import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ListingCard from "@/components/ListingCard";
import EventCard from "@/components/EventCard";
import JobCard from "@/components/JobCard";
import VerifiedBadge, { VERIFICATION_TIERS } from "@/components/VerifiedBadge";
import HireBadge from "@/components/HireBadge";
import StatusPill from "@/components/StatusPill";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Copy, Share2 } from "lucide-react";

export default function Profile() {
  const { user, loading, updateProfile } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [location, setLocation] = useState("");
  const [myListings, setMyListings] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [savedJobs, setSavedJobs] = useState([]);
  const [savedListings, setSavedListings] = useState([]);
  const [savedEvents, setSavedEvents] = useState([]);
  const [savedNeeds, setSavedNeeds] = useState([]);
  const [savedBusinesses, setSavedBusinesses] = useState([]);
  const [applications, setApplications] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [referrals, setReferrals] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => { if (!loading && !user) nav("/login"); }, [loading, user, nav]);
  useEffect(() => { if (user) { setName(user.name || ""); setBio(user.bio || ""); setLocation(user.location || ""); } }, [user]);
  useEffect(() => {
    if (!user) return;
    api.get("/listings", { params: { owner_id: user.id, limit: 50 } }).then((r) => setMyListings(r.data.items));
    api.get("/events", { params: { owner_id: user.id, upcoming: false, limit: 50 } }).then((r) => setMyEvents(r.data.items));
    api.get("/jobs", { params: { owner_id: user.id, limit: 50 } }).then((r) => setMyJobs(r.data.items));
    api.get("/saves/jobs").then((r) => setSavedJobs(r.data.items));
    api.get("/saves/listing").then((r) => setSavedListings(r.data.items)).catch(() => setSavedListings([]));
    api.get("/saves/event").then((r) => setSavedEvents(r.data.items)).catch(() => setSavedEvents([]));
    api.get("/saves/need").then((r) => setSavedNeeds(r.data.items)).catch(() => setSavedNeeds([]));
    api.get("/saves/business").then((r) => setSavedBusinesses(r.data.items)).catch(() => setSavedBusinesses([]));
    api.get("/applications/mine").then((r) => setApplications(r.data.items));
    api.get("/referrals/me").then((r) => setReferrals(r.data)).catch(() => setReferrals(null));
    api.get("/analytics/jobs/me").then((r) => setAnalytics(r.data)).catch(() => setAnalytics(null));
    if (user.role === "admin") {
      api.get("/admin/users").then((r) => setAllUsers(r.data.items));
    }
  }, [user]);

  if (!user) return null;

  const save = async () => {
    const r = await updateProfile({ name, bio, location });
    if (r.ok) toast.success("Profile updated");
    else toast.error(r.error);
  };

  const setUserTier = async (u, tier) => {
    try {
      const { data } = await api.patch(`/admin/users/${u.id}/verify`, null, { params: { tier } });
      setAllUsers((prev) => prev.map((x) => (x.id === u.id ? data : x)));
      toast.success(tier === "none" ? "Verification removed" : `Marked ${data.verification_tier}`);
    } catch { toast.error("Could not update verification"); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
      <div className="rounded-3xl bg-white border border-[#E2DED0] p-8 flex flex-col sm:flex-row gap-6 items-start">
        <div className="w-20 h-20 rounded-2xl bg-[#1E3A2F] text-[#F7F4EB] font-display text-3xl grid place-items-center">
          {(user.name || user.email).slice(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 w-full">
          <div className="text-xs uppercase tracking-[0.2em] text-[#C05A3D] font-semibold">Your profile</div>
          <h1 className="font-display text-3xl text-[#1E3A2F] flex items-center gap-2 flex-wrap">
            {user.name || user.email}
            {user.verified && <VerifiedBadge tier={user.verification_tier} size="sm" />}
            {user.hires_count > 0 && <HireBadge count={user.hires_count} />}
          </h1>
          <p className="text-sm text-[#5C5C5C] mt-1">{user.email}</p>
          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Display name</Label>
              <Input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label>Location</Label>
              <Input data-testid="profile-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Seattle, WA" />
            </div>
            <div className="sm:col-span-2">
              <Label>Bio</Label>
              <Textarea data-testid="profile-bio" value={bio} onChange={(e) => setBio(e.target.value)} rows={3} />
            </div>
            <div className="sm:col-span-2 flex items-center justify-between rounded-xl border border-[#E2DED0] p-4 bg-[#F7F4EB]/40">
              <div>
                <div className="text-sm font-medium text-[#1E3A2F]">Email me when I get a new inbox message</div>
                <div className="text-xs text-[#5C5C5C]">A friendly nudge with the message preview and a one-tap "Reply on ABM" button.</div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="profile-notify-messages"
                  checked={user.notify_messages !== false}
                  onChange={async (e) => {
                    const r = await updateProfile({ notify_messages: e.target.checked });
                    if (r.ok) toast.success(e.target.checked ? "Message emails on" : "Message emails off");
                    else toast.error(r.error);
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-[#E2DED0] peer-checked:bg-[#1E3A2F] rounded-full peer transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:bg-white after:rounded-full after:shadow after:transition-transform peer-checked:after:translate-x-5" />
              </label>
            </div>
          </div>
          <Button data-testid="profile-save" onClick={save} className="mt-4 abm-btn-primary rounded-full">Save changes</Button>
        </div>
      </div>

      {referrals && (
        <div className="mt-8 rounded-3xl bg-[#1E3A2F] text-[#F7F4EB] p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute inset-0 abm-kente opacity-[0.06]" />
          <div className="relative grid md:grid-cols-[1.2fr_0.8fr] gap-6 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#DDA74A] font-semibold inline-flex items-center gap-2">
                <Share2 className="w-3 h-3" /> Invite friends · earn credit
              </div>
              <h2 className="font-display text-2xl sm:text-3xl mt-1">Grow the community — get credit for every hire</h2>
              <p className="text-sm text-[#A3B3AA] mt-2 max-w-lg">Share your link. When a friend joins and posts a job, or when someone you referred gets hired, you get credit toward verified status.</p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <div className="rounded-full bg-white/10 border border-white/20 px-4 py-2 font-mono text-sm" data-testid="referral-code">
                  {referrals.referral_code}
                </div>
                <button
                  data-testid="referral-copy"
                  onClick={() => { navigator.clipboard.writeText(referrals.share_url); toast.success("Link copied"); }}
                  className="inline-flex items-center gap-1 rounded-full bg-[#DDA74A] text-[#1E3A2F] px-4 h-10 font-medium text-sm hover:bg-[#e6b869] transition"
                >
                  <Copy className="w-4 h-4" /> Copy invite link
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl bg-white/10 border border-white/10 p-4 text-center">
                <div className="text-4xl font-display text-[#DDA74A]" data-testid="referral-signups">{referrals.referral_signups}</div>
                <div className="text-xs uppercase tracking-[0.15em] text-[#A3B3AA] mt-1">Friends joined</div>
              </div>
              <div className="rounded-2xl bg-white/10 border border-white/10 p-4 text-center">
                <div className="text-4xl font-display text-[#DDA74A]" data-testid="referral-hires">{referrals.referral_hires}</div>
                <div className="text-xs uppercase tracking-[0.15em] text-[#A3B3AA] mt-1">Hired via you</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-10">
        <Tabs defaultValue="listings">
          <TabsList className="bg-[#F7F4EB] flex-wrap h-auto">
            <TabsTrigger value="listings" data-testid="profile-tab-listings">My listings ({myListings.length})</TabsTrigger>
            <TabsTrigger value="events" data-testid="profile-tab-events">My events ({myEvents.length})</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="profile-tab-jobs">My jobs ({myJobs.length})</TabsTrigger>
            <TabsTrigger value="saved" data-testid="profile-tab-saved">Saved ({savedJobs.length + savedListings.length + savedEvents.length + savedNeeds.length + savedBusinesses.length})</TabsTrigger>
            <TabsTrigger value="applications" data-testid="profile-tab-applications">Applications ({applications.length})</TabsTrigger>
            {analytics && <TabsTrigger value="analytics" data-testid="profile-tab-analytics">Analytics</TabsTrigger>}
            {user.role === "admin" && <TabsTrigger value="admin" data-testid="profile-tab-admin">Admin</TabsTrigger>}
          </TabsList>

          <TabsContent value="listings" className="mt-6">
            <div className="flex justify-end mb-4">
              <Link to="/post/listing" className="rounded-full abm-btn-primary px-4 h-10 leading-10 text-sm text-white">+ New listing</Link>
            </div>
            {myListings.length ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {myListings.map((l) => <ListingCard key={l.id} listing={l} />)}
              </div>
            ) : (<div className="text-center py-14 text-[#5C5C5C]">You haven't posted any listings yet.</div>)}
          </TabsContent>

          <TabsContent value="events" className="mt-6">
            <div className="flex justify-end mb-4">
              <Link to="/post/event" className="rounded-full abm-btn-pine px-4 h-10 leading-10 text-sm text-white">+ New event</Link>
            </div>
            {myEvents.length ? (
              <div className="grid md:grid-cols-2 gap-5">
                {myEvents.map((e) => <EventCard key={e.id} event={e} />)}
              </div>
            ) : (<div className="text-center py-14 text-[#5C5C5C]">You haven't posted any events yet.</div>)}
          </TabsContent>

          <TabsContent value="jobs" className="mt-6">
            <div className="flex justify-end mb-4">
              <Link to="/post/job" className="rounded-full abm-btn-primary px-4 h-10 leading-10 text-sm text-white">+ New job</Link>
            </div>
            {myJobs.length ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {myJobs.map((j) => <JobCard key={j.id} job={j} />)}
              </div>
            ) : (<div className="text-center py-14 text-[#5C5C5C]">You haven't posted any jobs yet.</div>)}
          </TabsContent>

          <TabsContent value="saved" className="mt-6 space-y-8">
            <SavedGroup title="Saved jobs" items={savedJobs} render={(j) => <JobCard key={j.id} job={j} />} emptyHint="Tap the heart on any job to save it here." testid="saved-jobs" />
            <SavedGroup title="Saved listings" items={savedListings} render={(l) => <ListingCard key={l.id} listing={l} />} emptyHint="Save marketplace items you love." testid="saved-listings" />
            <SavedGroup title="Saved events" items={savedEvents} render={(e) => <EventCard key={e.id} event={e} />} emptyHint="Save events to keep them handy." testid="saved-events" />
            <SavedGroup
              title="Saved needs & offers"
              items={savedNeeds}
              render={(n) => (
                <Link
                  key={n.id}
                  to={`/needs/${n.id}`}
                  data-testid={`saved-need-${n.id}`}
                  className="abm-card p-4 hover:border-[#C05A3D]"
                >
                  <div className="text-[10px] uppercase tracking-widest text-[#C05A3D] font-semibold">I {n.intent}</div>
                  <div className="font-display text-base mt-1 text-[#1E3A2F] line-clamp-2">{n.title}</div>
                </Link>
              )}
              emptyHint="Follow requests and offers that matter to you."
              testid="saved-needs"
            />
            <SavedGroup
              title="Saved businesses"
              items={savedBusinesses}
              render={(b) => (
                <Link
                  key={b.slug}
                  to={`/business/${b.slug}`}
                  data-testid={`saved-business-${b.slug}`}
                  className="abm-card p-4 hover:border-[#C05A3D]"
                >
                  <div className="text-[10px] uppercase tracking-widest text-[#C05A3D] font-semibold">{b.category}</div>
                  <div className="font-display text-base mt-1 text-[#1E3A2F] line-clamp-2">{b.name}</div>
                  <div className="text-xs text-[#5C5C5C] mt-1">{b.location}</div>
                </Link>
              )}
              emptyHint="Save community-recommended businesses to find them fast."
              testid="saved-businesses"
            />
          </TabsContent>

          <TabsContent value="applications" className="mt-6">
            {applications.length ? (
              <div className="space-y-3">
                {applications.map((a) => (
                  <Link
                    key={a.id}
                    to={`/jobs/${a.job_id}`}
                    data-testid={`my-application-${a.id}`}
                    className="abm-card p-5 flex items-center justify-between hover:border-[#C05A3D]"
                  >
                    <div>
                      <div className="font-display text-lg text-[#1E3A2F]">{a.job_title || "Application"}</div>
                      <div className="text-xs text-[#5C5C5C]">Applied {new Date(a.created_at).toLocaleDateString()}</div>
                    </div>
                    <StatusPill status={a.status} />
                  </Link>
                ))}
              </div>
            ) : (<div className="text-center py-14 text-[#5C5C5C]">No applications yet.</div>)}
          </TabsContent>

          {analytics && (
            <TabsContent value="analytics" className="mt-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <StatTile label="Total views" value={analytics.totals?.views ?? 0} />
                <StatTile label="Saves" value={analytics.totals?.saves ?? 0} />
                <StatTile label="Applies" value={analytics.totals?.applies ?? 0} />
                <StatTile label="Hire rate" value={`${analytics.totals?.hire_rate ?? 0}%`} />
              </div>
              <div className="rounded-2xl bg-white border border-[#E2DED0] overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F7F4EB] text-[#5C5C5C] text-xs uppercase tracking-widest">
                    <tr>
                      <th className="text-left px-5 py-3">Job</th>
                      <th className="text-right px-5 py-3">Views</th>
                      <th className="text-right px-5 py-3">Saves</th>
                      <th className="text-right px-5 py-3">Applies</th>
                      <th className="text-right px-5 py-3">Hires</th>
                      <th className="text-right px-5 py-3">Hire rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analytics.per_job || []).length === 0 ? (
                      <tr><td colSpan="6" className="text-center px-5 py-8 text-[#5C5C5C]">Post your first job to start seeing analytics.</td></tr>
                    ) : (analytics.per_job || []).map((j) => (
                      <tr key={j.id} className="border-t border-[#E2DED0]" data-testid={`analytics-row-${j.id}`}>
                        <td className="px-5 py-3">
                          <Link to={`/jobs/${j.id}`} className="font-medium text-[#1E3A2F] hover:text-[#C05A3D]">{j.title}</Link>
                          <div className="text-xs text-[#5C5C5C]">{j.role_category}</div>
                        </td>
                        <td className="px-5 py-3 text-right">{j.views}</td>
                        <td className="px-5 py-3 text-right">{j.saves}</td>
                        <td className="px-5 py-3 text-right">{j.applies}</td>
                        <td className="px-5 py-3 text-right">{j.hires}</td>
                        <td className="px-5 py-3 text-right font-semibold text-[#C05A3D]">{j.hire_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          )}

          {user.role === "admin" && (
            <TabsContent value="admin" className="mt-6">
              <div className="rounded-2xl bg-white border border-[#E2DED0] overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F7F4EB] text-[#5C5C5C] text-xs uppercase tracking-widest">
                    <tr>
                      <th className="text-left px-5 py-3">Name</th>
                      <th className="text-left px-5 py-3">Email</th>
                      <th className="text-left px-5 py-3">Role</th>
                      <th className="text-right px-5 py-3">Verification</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allUsers.map((u) => (
                      <tr key={u.id} className="border-t border-[#E2DED0]">
                        <td className="px-5 py-3 font-medium text-[#1A1A1A]">
                          <div className="flex items-center gap-2 flex-wrap">
                            {u.name || "—"}
                            {u.hires_count > 0 && <HireBadge count={u.hires_count} />}
                          </div>
                        </td>
                        <td className="px-5 py-3 text-[#5C5C5C]">{u.email}</td>
                        <td className="px-5 py-3 text-[#5C5C5C]">{u.role}</td>
                        <td className="px-5 py-3 text-right">
                          <Select value={u.verification_tier || "none"} onValueChange={(v) => setUserTier(u, v)}>
                            <SelectTrigger data-testid={`admin-tier-${u.id}`} className="w-[190px] h-9 rounded-full ml-auto"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {VERIFICATION_TIERS.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E2DED0] p-4">
      <div className="font-display text-2xl text-[#1E3A2F]">{value}</div>
      <div className="text-xs uppercase tracking-[0.15em] text-[#5C5C5C] mt-1">{label}</div>
    </div>
  );
}

function SavedGroup({ title, items, render, emptyHint, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="font-display text-xl text-[#1E3A2F]">{title}</h3>
        <span className="text-xs text-[#5C5C5C]">{items.length}</span>
      </div>
      {items.length ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(render)}
        </div>
      ) : (
        <div className="text-sm text-[#5C5C5C] italic rounded-2xl border border-dashed border-[#E2DED0] p-6">{emptyHint}</div>
      )}
    </div>
  );
}

